import { useCallback } from 'react'
import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
} from 'firebase/firestore'
import { firestore, auth } from '../config/firebase'
import { ISettingListItem, SettingListItem } from '../../../shared/types/setting.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

type AuditFields = 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
type SettingListItemUpdater = Partial<Omit<ISettingListItem, 'list' | AuditFields>>
type SettingListItemWithoutAudit = Omit<SettingListItem, AuditFields>

interface IUseSettingListReturn {
    list: (listName: string) => Promise<SettingListItem[]>
    create: (data: Omit<ISettingListItem, AuditFields>) => Promise<SettingListItemWithoutAudit>
    edit: (id: string, data: SettingListItemUpdater) => Promise<void>
}

const useSettingList = (): IUseSettingListReturn => {

    /**
     * Načte všechny položky daného číselníku dle názvu listu.
     * @param listName - Název číselníku (pole `list` v dokumentu)
     */
    const list = useCallback(async (listName: string): Promise<SettingListItem[]> => {
        const q = query(
            collection(firestore, COLLECTIONS.SETTING_LIST_ITEM),
            where('list', '==', listName),
            orderBy('label', 'asc')
        )
        const snapshot = await getDocs(q)
        return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as SettingListItem)
    }, [])

    /**
     * Vytvoří novou položku číselníku.
     * @param data - Data nové položky (včetně pole `list`)
     */
    const create = useCallback(async (data: Omit<ISettingListItem, AuditFields>): Promise<SettingListItemWithoutAudit> => {
        const ref = await addDoc(collection(firestore, COLLECTIONS.SETTING_LIST_ITEM), {
            ...data,
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.uid ?? null,
        })
        return { id: ref.id, ...data }
    }, [])

    /**
     * Upraví existující položku číselníku.
     * @param id - Firestore document ID položky
     * @param data - Pole k aktualizaci (bez `list` — ten se nemění)
     */
    const edit = useCallback(async (id: string, data: SettingListItemUpdater): Promise<void> => {
        const ref = doc(firestore, COLLECTIONS.SETTING_LIST_ITEM, id)
        await updateDoc(ref, {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid ?? null,
        })
    }, [])

    return { list, create, edit }
}

export default useSettingList
